import { useState } from 'react';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { Checkbox } from 'primereact/checkbox';
import { Card } from 'primereact/card';

const CertificationsStep = ({ data, onUpdate }) => {
    const [certifications, setCertifications] = useState(data.certifications || []);

    const addCertification = () => {
        const newCert = {
            certification_name: '',
            issuing_organization: '',
            credential_id: '',
            credential_url: '',
            issue_date: null,
            expiration_date: null,
            does_not_expire: false
        };

        const updated = [...certifications, newCert];
        setCertifications(updated);
        onUpdate({ certifications: updated });
    };

    const removeCertification = (index) => {
        const updated = certifications.filter((_, i) => i !== index);
        setCertifications(updated);
        onUpdate({ certifications: updated });
    };

    const updateCertification = (index, field, value) => {
        const updated = [...certifications];
        updated[index] = { ...updated[index], [field]: value };

        // If marking as does not expire, clear expiration_date
        if (field === 'does_not_expire' && value === true) {
            updated[index].expiration_date = null;
        }

        setCertifications(updated);
        onUpdate({ certifications: updated });
    };

    return (
        <div className="grid">
            <div className="col-12">
                <div className="flex justify-content-between align-items-center">
                    <div>
                        <h5 className="mt-0 mb-1">Certifications</h5>
                        <p className="text-600 mt-0">Add your professional certifications (optional)</p>
                    </div>
                    <Button
                        label="Add Certification"
                        icon="pi pi-plus"
                        onClick={addCertification}
                        className="p-button-outlined"
                    />
                </div>
            </div>

            {certifications.length === 0 && (
                <div className="col-12">
                    <div className="surface-100 border-round p-4 text-center">
                        <i className="pi pi-verified text-4xl text-400 mb-3"></i>
                        <p className="text-600 m-0">No certifications added yet</p>
                        <p className="text-500 text-sm m-0">Click "Add Certification" to add your professional credentials</p>
                    </div>
                </div>
            )}

            {certifications.map((cert, index) => (
                <div key={index} className="col-12">
                    <Card>
                        <div className="flex justify-content-between align-items-center mb-3">
                            <h6 className="m-0">Certification #{index + 1}</h6>
                            <Button
                                icon="pi pi-trash"
                                className="p-button-rounded p-button-text p-button-danger"
                                onClick={() => removeCertification(index)}
                            />
                        </div>

                        <div className="grid">
                            <div className="col-12 md:col-6">
                                <label htmlFor={`certification_name_${index}`} className="block mb-2">Certification Name <span className="text-red-500">*</span></label>
                                <InputText
                                    id={`certification_name_${index}`}
                                    name="certification_name"
                                    value={cert.certification_name}
                                    onChange={(e) => updateCertification(index, 'certification_name', e.target.value)}
                                    placeholder="e.g., Certified Financial Planner"
                                    className="w-full"
                                />
                            </div>

                            <div className="col-12 md:col-6">
                                <label htmlFor={`issuing_organization_${index}`} className="block mb-2">Issuing Organization <span className="text-red-500">*</span></label>
                                <InputText
                                    id={`issuing_organization_${index}`}
                                    name="issuing_organization"
                                    value={cert.issuing_organization}
                                    onChange={(e) => updateCertification(index, 'issuing_organization', e.target.value)}
                                    placeholder="e.g., Financial Planning Standards Board"
                                    className="w-full"
                                />
                            </div>

                            <div className="col-12 md:col-6">
                                <label htmlFor={`credential_id_${index}`} className="block mb-2">Credential ID</label>
                                <InputText
                                    id={`credential_id_${index}`}
                                    name="credential_id"
                                    value={cert.credential_id}
                                    onChange={(e) => updateCertification(index, 'credential_id', e.target.value)}
                                    placeholder="e.g., CFP12345"
                                    className="w-full"
                                />
                            </div>

                            <div className="col-12 md:col-6">
                                <label htmlFor={`credential_url_${index}`} className="block mb-2">Credential URL</label>
                                <InputText
                                    id={`credential_url_${index}`}
                                    name="credential_url"
                                    value={cert.credential_url}
                                    onChange={(e) => updateCertification(index, 'credential_url', e.target.value)}
                                    placeholder="https://verify.example.com/..."
                                    className="w-full"
                                />
                                <small className="text-500">Link to verify this certification</small>
                            </div>

                            <div className="col-12 md:col-6">
                                <label htmlFor={`issue_date_${index}`} className="block mb-2">Issue Date</label>
                                <Calendar
                                    id={`issue_date_${index}`}
                                    name="issue_date"
                                    value={cert.issue_date ? new Date(cert.issue_date) : null}
                                    onChange={(e) => updateCertification(index, 'issue_date', e.value)}
                                    dateFormat="dd/mm/yy"
                                    placeholder="Select date"
                                    className="w-full"
                                    maxDate={new Date()}
                                    showIcon
                                    readOnlyInput
                                />
                            </div>

                            <div className="col-12 md:col-6">
                                <label htmlFor={`expiration_date_${index}`} className="block mb-2">Expiration Date</label>
                                <Calendar
                                    id={`expiration_date_${index}`}
                                    name="expiration_date"
                                    value={cert.expiration_date ? new Date(cert.expiration_date) : null}
                                    onChange={(e) => updateCertification(index, 'expiration_date', e.value)}
                                    dateFormat="dd/mm/yy"
                                    placeholder="Select date"
                                    className="w-full"
                                    minDate={cert.issue_date ? new Date(cert.issue_date) : null}
                                    disabled={cert.does_not_expire}
                                    showIcon
                                    readOnlyInput
                                />
                            </div>

                            <div className="col-12">
                                <div className="flex align-items-center">
                                    <Checkbox
                                        inputId={`no-expiry-${index}`}
                                        checked={cert.does_not_expire}
                                        onChange={(e) => updateCertification(index, 'does_not_expire', e.checked)}
                                    />
                                    <label htmlFor={`no-expiry-${index}`} className="ml-2">This certification does not expire</label>
                                </div>
                            </div>
                        </div>
                    </Card>
                </div>
            ))}
        </div>
    );
};

export default CertificationsStep;
